import type {
  AbstractPartDefinition,
  DragConstraint,
} from "@project/emerge-types";
import { Canvas } from "@react-three/fiber";
import {
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Group, Vector3 } from "three";
import type { SceneDefinition } from "./Manufacturer";
import { useManufacturer } from "./ManufacturerProvider";
import { MissingPartDisplay } from "./MissingPartDisplay";
import { ProductFloorAligner } from "./ProductFloorAligner";
import { useProductStoreContext } from "./ProductStoreContext";
import { ProductViewerCamera } from "./ProductViewerCamera";
import { useProductViewerConfig } from "./ProductViewerConfigContext";
import { ProductViewerLighting } from "./ProductViewerLighting";
import { ProductViewerLoadingOverlay } from "./ProductViewerLoadingOverlay";
import {
  BlueprintRendererComponent,
  type DragDisplayValues,
  PartRendererComponent,
} from "./ProductViewerPartTree";
import { SceneRenderer } from "./SceneRenderer";
import {
  InteractiveGroup,
  useProductViewerGroupInteractions,
} from "./useProductViewerGroupInteractions";

export const ProductViewerXR = () => {
  const manufacturer = useManufacturer();
  const { loadingScreen } = useProductViewerConfig();
  const useProductStore = useProductStoreContext();
  const { hiddenPartTypes, selectedSceneId, updateUserOptions, buildVersion } =
    useProductStore();
  const product = useProductStore((state) => state.product);
  const productGroupRef = useRef<Group>(null);
  const [isInitialViewReady, setIsInitialViewReady] = useState(false);
  const [initialFitToken, setInitialFitToken] = useState(0);
  const isLoadingOverlayEnabled = loadingScreen !== false;
  const isCanvasVisible = !isLoadingOverlayEnabled || isInitialViewReady;

  useEffect(() => {
    if (!product) return;
    setIsInitialViewReady(false);
    setInitialFitToken((token) => token + 1);
  }, [product]);

  const handleInitialFitComplete = useCallback(() => {
    setIsInitialViewReady(true);
  }, []);

  const selectedScene = useMemo(() => {
    if (!manufacturer.scenes) {
      return undefined;
    }

    const sceneIdToFind = selectedSceneId ?? "default";
    return manufacturer.scenes.find(
      (scene: SceneDefinition) => scene.id === sceneIdToFind,
    );
  }, [selectedSceneId, manufacturer.scenes]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: buildVersion is intentionally included to trigger recomputation after incremental builds
  const partsByGroup = useMemo(() => {
    if (!product?.partDefinitions) return {};
    const partsArray = Array.from(product.partDefinitions.values());
    return partsArray.reduce(
      (acc, part) => {
        const group = part.group ?? "default";
        acc[group] ??= [];
        acc[group].push(part);
        return acc;
      },
      {} as Record<string, AbstractPartDefinition<string>[]>,
    );
  }, [product, product?.partDefinitions, buildVersion]);

  const { groupStates, toggleGroup, registerGroup } =
    useProductViewerGroupInteractions(product);

  const handlePartDragUpdate = useCallback(
    (
      partId: string,
      newPosition: Vector3,
      constraint: DragConstraint,
      settingsContext: Record<string, unknown> | undefined,
    ): DragDisplayValues | undefined => {
      if (!product) return;

      const result = product.handleDragUpdate(
        partId,
        newPosition,
        constraint,
        settingsContext,
      );

      if (result) {
        void updateUserOptions(result.settingsUpdate, { coalesce: true });
        return result.displayValues;
      }
    },
    [product, updateUserOptions],
  );

  if (!product) return null;

  return (
    <div
      className="relative min-h-0 min-w-0 flex-1 overflow-hidden"
      style={{
        flex: "1 1 0%",
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "hidden",
        position: "relative",
        width: "100%",
      }}
    >
      <Canvas
        className={`h-full w-full transition-opacity duration-300 ${
          isCanvasVisible ? "opacity-100" : "opacity-0"
        }`}
        style={{
          height: "100%",
          opacity: isCanvasVisible ? 1 : 0,
          transition: "opacity 300ms ease",
          width: "100%",
        }}
        camera={{ position: [0.75, 1.125, 1.875], fov: 50, near: 0.01 }}
        gl={{ antialias: true }}
        onCreated={({ gl }) => {
          gl.setClearColor(0x000000, 0);
        }}
      >
        <ProductViewerLighting />

        <SceneRenderer scene={selectedScene} resolution={512} />
        <ProductViewerCamera
          key={initialFitToken}
          onInitialFitComplete={handleInitialFitComplete}
        >
          <ProductFloorAligner>
            <group ref={productGroupRef}>
              {product.blueprint &&
              manufacturer.blueprintRenderers[product.blueprint.type] ? (
                <BlueprintRendererComponent
                  blueprint={product.blueprint}
                  renderer={
                    manufacturer.blueprintRenderers[product.blueprint.type]
                  }
                  isHidden={hiddenPartTypes.includes(product.blueprint.type)}
                />
              ) : null}
              {manufacturer.measurementRenderer &&
              !hiddenPartTypes.includes("Measurements")
                ? createElement(manufacturer.measurementRenderer, {
                    target: productGroupRef,
                    visible: true,
                    key: "measurement",
                  })
                : null}
              {Object.entries(partsByGroup).map(([group, parts]) => {
                return (
                  <InteractiveGroup
                    key={`group-${group}`}
                    groupName={group}
                    groupState={groupStates[group]}
                    onToggle={toggleGroup}
                    onRegister={registerGroup}
                  >
                    {parts.map((part) => {
                      const isHidden = hiddenPartTypes.includes(part.type);
                      part.hidden = isHidden;
                      const partRenderer =
                        manufacturer.partRenderers[part.type];

                      if (partRenderer) {
                        return (
                          <PartRendererComponent
                            key={`part-${part.id || part.type}-${group}`}
                            part={part}
                            renderer={partRenderer}
                            group={group}
                            onDragUpdate={handlePartDragUpdate}
                          />
                        );
                      }
                      return (
                        <MissingPartDisplay
                          key={`missing-${part.id || part.type}-${group}`}
                        />
                      );
                    })}
                  </InteractiveGroup>
                );
              })}
            </group>
          </ProductFloorAligner>
        </ProductViewerCamera>
      </Canvas>
      {isLoadingOverlayEnabled ? (
        <ProductViewerLoadingOverlay visible={!isInitialViewReady}>
          {loadingScreen}
        </ProductViewerLoadingOverlay>
      ) : null}
    </div>
  );
};
